import React, { useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBio() {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/site").then((r) => setSite(r.data));
  }, []);

  if (!site) return <div className="text-champagne/60">Loading...</div>;

  const update = (k, v) => setSite({ ...site, [k]: v });
  const updateStat = (k, v) => setSite({ ...site, stats: { ...site.stats, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/site", site);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const addStat = () => {
    const key = prompt("New stat label (e.g. 'Favorite flower'):");
    if (!key) return;
    updateStat(key, "");
  };

  const removeStat = (k) => {
    const copy = { ...site.stats };
    delete copy[k];
    setSite({ ...site, stats: copy });
  };

  const fieldCls = "w-full bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne";
  const labelCls = "text-[10px] tracking-[0.28em] uppercase text-pink-blush mb-2 block";

  return (
    <div data-testid="admin-bio-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Profile</div>
          <h1 className="font-display text-4xl text-champagne">About & <span className="italic text-pink-hot">Bio</span></h1>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-7 py-3 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all disabled:opacity-60"
          data-testid="save-bio"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="space-y-8">
        <div className="glass-panel rounded-md p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Display Name</label>
              <input value={site.name} onChange={(e) => update("name", e.target.value)} className={fieldCls} data-testid="bio-name" />
            </div>
            <div>
              <label className={labelCls}>Tagline</label>
              <input value={site.tagline} onChange={(e) => update("tagline", e.target.value)} className={fieldCls} data-testid="bio-tagline" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Hero Subtitle</label>
            <input value={site.hero_subtitle} onChange={(e) => update("hero_subtitle", e.target.value)} className={fieldCls} data-testid="bio-hero-subtitle" />
          </div>
          <div>
            <label className={labelCls}>Short Bio</label>
            <textarea rows={3} value={site.about_bio} onChange={(e) => update("about_bio", e.target.value)} className={fieldCls} data-testid="bio-short" />
          </div>
          <div>
            <label className={labelCls}>Long Bio / Story</label>
            <textarea rows={6} value={site.about_long} onChange={(e) => update("about_long", e.target.value)} className={fieldCls} data-testid="bio-long" />
          </div>
        </div>

        <div className="glass-panel rounded-md p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush">Stats</div>
            <button onClick={addStat} className="text-xs text-pink-hot tracking-[0.25em] uppercase" data-testid="add-stat">+ Add Stat</button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.entries(site.stats || {}).map(([k, v]) => (
              <div key={k} className="flex gap-2 items-center">
                <input
                  value={k}
                  disabled
                  className="w-1/3 bg-noir-elevated border border-rosegold/20 px-3 py-2 text-xs rounded-sm text-champagne/80"
                />
                <input
                  value={v}
                  onChange={(e) => updateStat(k, e.target.value)}
                  className="flex-1 bg-noir-base border border-rosegold/20 px-3 py-2 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne"
                  data-testid={`stat-${k}`}
                />
                <button onClick={() => removeStat(k)} className="text-rosegold/70 hover:text-pink-hot text-xs">✕</button>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel rounded-md p-6 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush">Contact & Policy</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input value={site.contact_email} onChange={(e) => update("contact_email", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={site.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Instagram</label>
              <input value={site.instagram} onChange={(e) => update("instagram", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>Twitter</label>
              <input value={site.twitter} onChange={(e) => update("twitter", e.target.value)} className={fieldCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Booking Policy</label>
            <textarea rows={3} value={site.booking_policy} onChange={(e) => update("booking_policy", e.target.value)} className={fieldCls} data-testid="bio-policy" />
          </div>
        </div>

        <div className="glass-panel rounded-md p-6 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush">E-Transfer Deposits</div>
          <p className="text-xs text-champagne/70 leading-relaxed">
            This is the email address where clients will send their e-Transfer deposits. Update it here any time.
          </p>
          <div>
            <label className={labelCls}>E-Transfer email</label>
            <input
              value={site.etransfer_email || ""}
              onChange={(e) => update("etransfer_email", e.target.value)}
              className={fieldCls}
              placeholder="layla.etransfer@example.com"
              data-testid="bio-etransfer-email"
            />
          </div>
          <div>
            <label className={labelCls}>Instructions shown to clients</label>
            <textarea
              rows={3}
              value={site.etransfer_instructions || ""}
              onChange={(e) => update("etransfer_instructions", e.target.value)}
              className={fieldCls}
              data-testid="bio-etransfer-instructions"
            />
          </div>
        </div>
        <div className="glass-panel rounded-md p-6 space-y-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush">Music · Spotify Player</div>
          <p className="text-xs text-champagne/70 leading-relaxed">
            The background music player in the bottom-left corner of your site plays a Spotify playlist of your choice.
            Paste the <strong>playlist ID</strong> below — it's the string after <code className="text-pink-blush">/playlist/</code> in any Spotify playlist URL.
          </p>
          <div>
            <label className={labelCls}>Spotify playlist ID</label>
            <input
              value={site.spotify_playlist_id || ""}
              onChange={(e) => update("spotify_playlist_id", e.target.value)}
              className={fieldCls}
              placeholder="e.g. 37i9dQZF1DX6J5NfMJS675"
              data-testid="bio-spotify-id"
            />
            <div className="text-[10px] text-champagne/50 mt-2">
              Example: <code className="text-pink-blush">37i9dQZF1DX6J5NfMJS675</code> (Tech House) · <code className="text-pink-blush">37i9dQZF1DX2TRYkJECvfC</code> (Deep House Relax) · or paste any public playlist's ID.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
